// @flow
/**
 * Maps resident and applicant pet records to one view model.
 * The view model maps the API `breedId` field to `petBreedId`.
 */
import type {
  ApplicantPetApiRecord,
  ApplicantPetRecord,
  HouseholdPetApiRecord,
  PetKind,
  PetStatus,
  PetViewModel,
  ResidentPetRecord,
} from '../types';

const isNonEmptyObject = (value: mixed): boolean =>
  typeof value === 'object' && value !== null;

/** Maps the fields shared by resident and applicant pet records. */
const buildPetViewModel = ({
  id,
  applicantId,
  kind,
  status,
  startDate,
  endDate,
  pet,
}: {
  id: string,
  applicantId: ?string,
  kind: PetKind,
  status: PetStatus,
  startDate: ?string,
  endDate: ?string,
  pet: ResidentPetRecord | ApplicantPetRecord,
}): PetViewModel => ({
  id,
  applicantId: applicantId ?? null,
  kind,
  status,
  name: pet.name ?? '',
  petTypeId: pet.petTypeId ?? null,
  petTypeName: pet.petType?.name ?? null,
  // Normalize the API field name.
  petBreedId: pet.breedId ?? null,
  petBreedName: pet.petBreed?.name ?? null,
  weight: pet.weight ?? null,
  petTag: pet.petTag ?? null,
  // Normalize boolean-compatible API values.
  isServiceAnimal: Boolean(pet.isServiceAnimal),
  // Dates belong to the containing household or applicant entry.
  startDate: startDate ?? null,
  endDate: endDate ?? null,
  raw: pet,
});

/** Returns current pets before prior pets and preserves source order. */
export const buildResidentPets = (
  household: ?Array<HouseholdPetApiRecord>,
): Array<PetViewModel> => {
  const entries = (household ?? []).filter(
    (entry) =>
      isNonEmptyObject(entry) &&
      entry?.type === 'pet' &&
      isNonEmptyObject(entry?.residentPet),
  );

  const toViewModel = (entry) => {
    const isPrior = entry.status === 'Prior';
    return buildPetViewModel({
      id: entry.id,
      // The hard-delete route requires the Applicant ID.
      applicantId: entry.applicantId,
      kind: isPrior ? 'prior' : 'resident',
      status: isPrior ? 'Prior' : 'Current',
      startDate: entry.startDate,
      endDate: entry.endDate,
      // The filter requires this record.
      pet: entry.residentPet ?? {},
    });
  };

  return [
    ...entries.filter((entry) => entry.status !== 'Prior').map(toViewModel),
    ...entries.filter((entry) => entry.status === 'Prior').map(toViewModel),
  ];
};

/** Maps pending or denied applicants by Applicant ID. */
const buildApplicantPets = (
  applicants: ?Array<ApplicantPetApiRecord>,
  kind: PetKind,
  status: PetStatus,
): Array<PetViewModel> =>
  (applicants ?? [])
    .filter(
      (entry) =>
        isNonEmptyObject(entry) && isNonEmptyObject(entry?.applicantPet),
    )
    .map((entry) =>
      buildPetViewModel({
        id: entry.id,
        // The entry ID is the Applicant ID.
        applicantId: entry.id,
        kind,
        status,
        startDate: entry.startDate,
        endDate: entry.endDate,
        pet: entry.applicantPet ?? {},
      }),
    );

export const buildApplicationPets = (
  applicants: ?Array<ApplicantPetApiRecord>,
): Array<PetViewModel> =>
  buildApplicantPets(applicants, 'applicant', 'Pending');

export const buildPendingPets = (
  pendingApplicants: ?Array<ApplicantPetApiRecord>,
): Array<PetViewModel> =>
  buildApplicantPets(pendingApplicants, 'pending', 'Pending');

export const buildDeniedPets = (
  canceledApplicants: ?Array<ApplicantPetApiRecord>,
): Array<PetViewModel> =>
  buildApplicantPets(canceledApplicants, 'denied', 'Denied');

/** Returns Current, Prior, Pending, and Denied groups in that order. */
export const buildHouseholdPets = ({
  household,
  pendingApplicants,
  canceledApplicants,
}: {
  household: ?Array<HouseholdPetApiRecord>,
  pendingApplicants: ?Array<ApplicantPetApiRecord>,
  canceledApplicants: ?Array<ApplicantPetApiRecord>,
}): Array<PetViewModel> => [
  ...buildResidentPets(household),
  ...buildPendingPets(pendingApplicants),
  ...buildDeniedPets(canceledApplicants),
];
